import theme from "../theme/theme";

// Derives a single, participant-facing display status for a meeting from the
// raw data the API returns (schedule.status: pending|finished|cancelled,
// per-invitee status: pending|accepted|declined, and the pending-reschedule
// fields proposedStartDate / rescheduleRequestedById).
//
// Returned label is one of: Confirmed | Declined | Rescheduled | Pending |
// Finished — each with a theme color so the badge is colour-coded consistently
// across the meeting list and the details modal.
export const getMeetingDisplayStatus = (meeting, userId) => {
  if (!meeting) return { label: "Pending", color: theme.palette.warning.main };

  // A cancelled meeting (e.g. everyone declined) reads as Declined.
  if (meeting.status === "cancelled") {
    return { label: "Declined", color: theme.palette.error.main };
  }

  if (meeting.status === "finished") {
    return { label: "Finished", color: theme.palette.gray?.main || "#4d4d4d" };
  }

  // A pending reschedule request (invitee proposed a new time) takes priority.
  if (meeting.proposedStartDate && meeting.rescheduleRequestedById) {
    return { label: "Rescheduled", color: theme.palette.warning.main };
  }

  const invitations = meeting.invitations || [];
  const isOwner = meeting.ownerId === userId;

  if (isOwner) {
    // Owner: confirmed once at least one invitee has accepted.
    if (invitations.some((inv) => inv.status === "accepted")) {
      return { label: "Confirmed", color: theme.palette.success.main };
    }
    return { label: "Pending", color: theme.palette.warning.main };
  }

  // Invitee: reflect my own response.
  const mine = invitations.find((inv) => inv.registrationId === userId);
  if (mine?.status === "accepted") {
    return { label: "Confirmed", color: theme.palette.success.main };
  }
  if (mine?.status === "declined") {
    return { label: "Declined", color: theme.palette.error.main };
  }
  return { label: "Pending", color: theme.palette.warning.main };
};

// Compares two meetings by start date & time (ascending / soonest first).
// Times are stored as naive wall-clock strings, so lexical/Date comparison is
// consistent across the app.
export const byStartDateAsc = (a, b) => {
  const av = a?.startDate ? new Date(a.startDate).getTime() : Infinity;
  const bv = b?.startDate ? new Date(b.startDate).getTime() : Infinity;
  return av - bv;
};

// Validates picked meeting times against the conference rules (mirrors the
// backend / mobile app): October 4–7, 2026; a START must be 9:00 AM–4:45 PM;
// an END must be after 9:00 AM and no later than 5:00 PM, and after the start;
// start and end must be the SAME day. Returns an error string, or null when
// valid. The Date objects are read in local (wall-clock) terms — the same
// values shown in the picker.
export const validateMeetingTimes = (start, end) => {
  if (!start || !end) return "Please select a start and end time";

  const OPEN = 9 * 60; // 09:00 earliest start
  const START_CLOSE = 16 * 60 + 45; // 16:45 latest start (4:45 PM)
  const END_CLOSE = 17 * 60; // 17:00 latest end (5 PM)

  const onDay = (d) =>
    d.getFullYear() === 2026 &&
    d.getMonth() === 9 && // October (0-indexed)
    d.getDate() >= 4 &&
    d.getDate() <= 7;
  const mins = (d) => d.getHours() * 60 + d.getMinutes();
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (!sameDay(start, end)) {
    return "The meeting start and end must be on the same day";
  }
  const startOk = onDay(start) && mins(start) >= OPEN && mins(start) <= START_CLOSE;
  const endOk = onDay(end) && mins(end) > OPEN && mins(end) <= END_CLOSE;
  if (!startOk || !endOk) {
    return "Meetings must start from 9:00 AM and end by 5:00 PM, and end after the start time, on October 4–7, 2026";
  }
  if (!(start.getTime() < end.getTime())) {
    return "Start time must be before end time";
  }
  return null;
};
